import { useCallback, useEffect, useRef, useState } from 'react';
import { sessionCoordinator, isApiError, isUnauthorizedError } from '@shared/api/http';
import {
  completeOAuthLogin,
  completeRegistration,
  fetchCurrentUser,
  getRegistrationReceipt,
  loginWithPassword,
  logoutCurrentSession,
  registerAccount,
  resendRegistrationEmail,
  requestPasswordResetCode,
  submitPasswordReset,
} from './api';
import type {
  AuthSessionState,
  AuthSessionUser,
  LoginCredentials,
  OAuthCallbackPayload,
  PasswordResetPayload,
  RegisterPayload,
} from './types';

const checkingAuthState: AuthSessionState = {
  status: 'checking',
  user: null,
  error: null,
};

export function getAuthErrorMessage(error: unknown) {
  if (isApiError(error)) {
    const code = (error.body as { data?: { code?: string } } | undefined)?.data?.code;
    const messages: Record<string, string> = {
      INVITE_INVALID: '邀请码无效或已用完', EMAIL_REGISTERED: '该邮箱已注册',
      INVALID_VERIFICATION_CODE: '验证码错误或已失效', VERIFICATION_LOCKED: '验证码错误次数过多，请重新发送验证码',
      REGISTRATION_UNAVAILABLE: '本次注册已失效，请重新开始', INVALID_PASSWORD: '密码不符合要求',
      RATE_LIMITED: '操作太频繁，请稍后再试',
    };
    if (code && messages[code]) return messages[code];
  }
  if (isApiError(error) && [502, 503, 504].includes(error.status)) {
    return '服务暂时连接不上，请稍后再试';
  }
  if (isApiError(error) && error.status >= 500) {
    return '服务暂时开小差了，请稍后再试';
  }
  return error instanceof Error ? error.message : String(error);
}

export function createAnonymousAuthState(error: unknown = null): AuthSessionState {
  return {
    status: 'anonymous',
    user: null,
    error: error == null ? null : getAuthErrorMessage(error),
  };
}

export async function loadInitialAuthState(
  apiBase: string,
  signal?: AbortSignal,
): Promise<AuthSessionState> {
  try {
    const user = await fetchCurrentUser(apiBase, signal);
    return { status: 'authenticated', user, error: null };
  } catch (error) {
    if (signal?.aborted) {
      throw signal.reason ?? error;
    }
    if (isUnauthorizedError(error)) {
      return { status: 'anonymous', user: null, error: null };
    }
    return {status:'checking',user:null,error:getAuthErrorMessage(error)};
  }
}

export function useAuthSession(apiBase: string, initialAuthState?: AuthSessionState) {
  const [state, setState] = useState<AuthSessionState>(initialAuthState ?? checkingAuthState);
  const requestIdRef = useRef(0);
  const shouldSkipInitialRefreshRef = useRef(Boolean(initialAuthState));

  const refreshSession = useCallback(async (): Promise<AuthSessionUser | null> => {
    const requestId = ++requestIdRef.current;
    setState((prev) => (
      prev.user
        ? { ...prev, status: 'authenticated', error: null }
        : { ...prev, status: 'checking', error: null }
    ));

    try {
      const user = await fetchCurrentUser(apiBase);
      if (requestIdRef.current !== requestId) {
        return null;
      }
      setState({ status: 'authenticated', user, error: null });
      return user;
    } catch (error) {
      if (requestIdRef.current !== requestId) {
        return null;
      }
      if (isUnauthorizedError(error)) {
        setState({ status: 'anonymous', user: null, error: null });
        return null;
      }
      setState((prev) => (
        prev.user
          ? { status: 'authenticated', user: prev.user, error: getAuthErrorMessage(error) }
          : { status: 'checking', user: null, error: getAuthErrorMessage(error) }
      ));
      throw error;
    }
  }, [apiBase]);

  useEffect(() => {
    if (shouldSkipInitialRefreshRef.current) {
      shouldSkipInitialRefreshRef.current = false;
      return;
    }
    void refreshSession().catch(() => {
      /* state already contains the user-facing error */
    });
  }, [refreshSession]);

  useEffect(() => {
    const coordinator = sessionCoordinator(apiBase);
    const changed = () => {
      if (coordinator.read()?.status === 'out') {
        ++requestIdRef.current;
        setState({status:'anonymous',user:null,error:null});
      }
    };
    let observedEpoch = coordinator.read()?.epoch;
    const otherTabChanged = () => {
      const meta = coordinator.read();
      if (meta?.epoch === observedEpoch) return;
      observedEpoch = meta?.epoch;
      if (meta?.status === 'active') {
        setState({status:'checking',user:null,error:null});
        void refreshSession().catch(() => {});
      } else changed();
    };
    const resume = () => { if (!document.hidden) void refreshSession().catch(() => {}); };
    window.addEventListener('locs-session-change', changed);
    window.addEventListener('online', resume);
    window.addEventListener('storage', otherTabChanged);
    document.addEventListener('visibilitychange', resume);
    return () => {
      window.removeEventListener('locs-session-change', changed);
      window.removeEventListener('online', resume);
      window.removeEventListener('storage', otherTabChanged);
      document.removeEventListener('visibilitychange', resume);
    };
  }, [apiBase, refreshSession]);

  const login = useCallback(async (credentials: LoginCredentials): Promise<AuthSessionUser> => {
    const requestId = ++requestIdRef.current;
    setState((prev) => ({ ...prev, status: 'submitting', error: null }));
    try {
      await loginWithPassword(credentials, apiBase);
      const user = await fetchCurrentUser(apiBase);
      if (requestIdRef.current !== requestId) {
        return user;
      }
      setState({ status: 'authenticated', user, error: null });
      return user;
    } catch (error) {
      if (requestIdRef.current !== requestId) {
        throw error;
      }
      setState({ status: 'anonymous', user: null, error: getAuthErrorMessage(error) });
      throw error;
    }
  }, [apiBase]);

  const loginWithOAuth = useCallback(async (payload: OAuthCallbackPayload): Promise<AuthSessionUser> => {
    const requestId = ++requestIdRef.current;
    setState((prev) => ({ ...prev, status: 'submitting', error: null }));
    try {
      await completeOAuthLogin(payload, apiBase);
      const user = await fetchCurrentUser(apiBase);
      if (requestIdRef.current !== requestId) {
        return user;
      }
      setState({ status: 'authenticated', user, error: null });
      return user;
    } catch (error) {
      if (requestIdRef.current !== requestId) {
        throw error;
      }
      setState({ status: 'anonymous', user: null, error: getAuthErrorMessage(error) });
      throw error;
    }
  }, [apiBase]);

  const logout = useCallback(async (): Promise<void> => {
    const requestId = ++requestIdRef.current;
    setState({status:'anonymous',user:null,error:null});
    try {
      await logoutCurrentSession(apiBase);
      if (requestIdRef.current !== requestId) {
        return;
      }
      setState({ status: 'anonymous', user: null, error: null });
    } catch (error) {
      if (requestIdRef.current !== requestId) {
        throw error;
      }
      setState({status:'anonymous',user:null,error:getAuthErrorMessage(error)});
      throw error;
    }
  }, [apiBase]);

  const register = useCallback(async (payload: RegisterPayload): Promise<void> => {
    const requestId = ++requestIdRef.current;
    setState({ status: 'submitting', user: null, error: null });
    try {
      await registerAccount(payload, apiBase);
      if (requestIdRef.current === requestId) {
        setState({ status: 'anonymous', user: null, error: null });
      }
    } catch (error) {
      if (requestIdRef.current === requestId) {
        setState({ status: 'anonymous', user: null, error: getAuthErrorMessage(error) });
      }
      throw error;
    }
  }, [apiBase]);

  const finishRegistration = useCallback(async (code: string, password: string): Promise<void> => {
    const requestId = ++requestIdRef.current;
    setState({ status: 'submitting', user: null, error: null });
    try {
      try {
        await completeRegistration(code, password, apiBase);
      } catch (error) {
        if (!isApiError(error) || error.status >= 500) {
          const receipt = await getRegistrationReceipt(apiBase).catch(() => null);
          if (receipt?.status !== 'completed') throw error;
        } else throw error;
      }
      if (requestIdRef.current === requestId) setState({ status: 'anonymous', user: null, error: null });
    } catch (error) {
      if (requestIdRef.current === requestId) setState({ status: 'anonymous', user: null, error: getAuthErrorMessage(error) });
      throw error;
    }
  }, [apiBase]);

  const requestPasswordReset = useCallback(async (email: string): Promise<void> => {
    const requestId = ++requestIdRef.current;
    setState((prev) => ({ ...prev, status: 'submitting', error: null }));
    try {
      await requestPasswordResetCode(email, apiBase);
      if (requestIdRef.current === requestId) {
        setState((prev) => ({ ...prev, status: 'anonymous', error: null }));
      }
    } catch (error) {
      if (requestIdRef.current === requestId) {
        setState({ status: 'anonymous', user: null, error: getAuthErrorMessage(error) });
      }
      throw error;
    }
  }, [apiBase]);

  const resetPassword = useCallback(async (payload: PasswordResetPayload): Promise<void> => {
    const requestId = ++requestIdRef.current;
    setState((prev) => ({ ...prev, status: 'submitting', error: null }));
    try {
      await submitPasswordReset(payload, apiBase);
      if (requestIdRef.current === requestId) {
        setState({ status: 'anonymous', user: null, error: null });
      }
    } catch (error) {
      if (requestIdRef.current === requestId) {
        setState({ status: 'anonymous', user: null, error: getAuthErrorMessage(error) });
      }
      throw error;
    }
  }, [apiBase]);

  return {
    state,
    refreshSession,
    login,
    loginWithOAuth,
    logout,
    register,
    finishRegistration,
    resendRegistration: () => resendRegistrationEmail(apiBase),
    requestPasswordReset,
    resetPassword,
  };
}
